/**
 * FmSelector - the one single-choice selector: segmented pills, radio dots or chips.
 *
 * Source ref: plans/forestmodeler-design-system/spacing-surface-rules.md RULE 4.
 *   - "segmented" (role=tablist / tab): pill row for switching a view or lens.
 *   - "radio"     (role=radiogroup, native radio inputs): finer secondary filters.
 *   - "chips"     (role=radiogroup / radio buttons): wrapping chip row for many options.
 *
 * Every variant is controlled, takes a required accessible name, keeps one tab
 * stop (roving tabindex), and moves the selection with the arrow keys plus
 * Home/End. Labels are ReactNode passed in by the consumer (i18n).
 *
 * Segmented and chips take an `on-dark` tone for deep and glass-deep surfaces:
 * light ink on the dark ground and the brand-green fill for the one chosen
 * option. Inside an FmSurface glass-deep the tone is chosen automatically.
 */

import React, { useId, useRef } from 'react';
import { FM_SELECTOR_TONES, useFmSurface, type FmSelectorTone } from './surfaceContract';
import './FmSelector.css';

export type { FmSelectorTone } from './surfaceContract';

export interface FmSelectorItem {
  /** Controlled value, matched to the consumer's `value` prop. */
  value: string;
  /** Display label. Pass i18n-translated ReactNode from the consumer. */
  label: React.ReactNode;
  /** Optional leading icon. */
  icon?: React.ReactNode;
  disabled?: boolean;
  'data-testid'?: string;
}

export interface FmSelectorProps {
  variant: 'segmented' | 'radio' | 'chips';
  /** Currently selected value; `null` when nothing is selected yet. */
  value: string | null;
  onChange: (value: string) => void;
  items: FmSelectorItem[];
  /** Required accessible name for the group. Ignored when labelledBy is set. */
  label: string;
  /** ID of visible text that labels this group. */
  labelledBy?: string;
  /**
   * Ground the selector sits on. `on-dark` is for segmented and chips on a deep
   * band or glass-deep surface; omitted, an FmSurface glass-deep ancestor picks
   * `on-dark` and anything else `default`.
   */
  tone?: FmSelectorTone;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  'data-testid'?: string;
}

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

const FORWARD_KEYS = new Set(['ArrowRight', 'ArrowDown']);
const BACKWARD_KEYS = new Set(['ArrowLeft', 'ArrowUp']);

function enabledIndex(items: FmSelectorItem[], from: number, dir: 1 | -1): number {
  const len = items.length;
  let idx = from;
  for (let attempts = 0; attempts < len; attempts += 1) {
    idx = (idx + dir + len) % len;
    if (!items[idx]?.disabled) return idx;
  }
  return from;
}

/** Index the arrow/Home/End key moves to, or null for any other key. */
function targetIndex(items: FmSelectorItem[], current: number, key: string): number | null {
  if (FORWARD_KEYS.has(key)) return enabledIndex(items, current, 1);
  if (BACKWARD_KEYS.has(key)) return enabledIndex(items, current, -1);
  if (key === 'Home') return enabledIndex(items, -1, 1);
  if (key === 'End') return enabledIndex(items, items.length, -1);
  return null;
}

/**
 * Roving focus shared by all variants: the selected item (or the first enabled
 * one when nothing is selected) is the single tab stop, and a navigation key
 * focuses and selects the target item.
 */
function useRovingSelection(items: FmSelectorItem[], value: string | null, onChange: (value: string) => void) {
  const refs = useRef<Array<HTMLElement | null>>([]);
  const selectedIndex = items.findIndex((item) => item.value === value);
  const tabStop = selectedIndex >= 0 ? selectedIndex : items.findIndex((item) => !item.disabled);
  const onKeyDown = (event: React.KeyboardEvent, index: number): void => {
    const next = targetIndex(items, index, event.key);
    if (next === null) return;
    event.preventDefault();
    refs.current[next]?.focus();
    onChange(items[next].value);
  };
  const register = (index: number) => (node: HTMLElement | null) => {
    refs.current[index] = node;
  };
  return { tabStop, onKeyDown, register };
}

const SegmentedOrChips: React.FC<FmSelectorProps & { variant: 'segmented' | 'chips' }> = ({
  variant,
  value,
  onChange,
  items,
  label,
  labelledBy,
  tone,
  className,
  'data-testid': dataTestId,
}) => {
  const { tabStop, onKeyDown, register } = useRovingSelection(items, value, onChange);
  const segmented = variant === 'segmented';
  const block = segmented ? 'fm-selector-seg' : 'fm-selector-chips';
  return (
    <div
      role={segmented ? 'tablist' : 'radiogroup'}
      aria-orientation={segmented ? 'horizontal' : undefined}
      aria-label={labelledBy ? undefined : label}
      aria-labelledby={labelledBy}
      className={cx(block, tone === 'on-dark' && 'fm-selector--on-dark', className)}
      data-tone={tone}
      data-testid={dataTestId}
    >
      {items.map((item, idx) => {
        const isActive = item.value === value;
        return (
          <button
            key={item.value}
            ref={register(idx)}
            type="button"
            role={segmented ? 'tab' : 'radio'}
            aria-selected={segmented ? isActive : undefined}
            aria-checked={segmented ? undefined : isActive}
            disabled={item.disabled}
            tabIndex={idx === tabStop ? 0 : -1}
            className={cx(`${block}__btn`, isActive && `${block}__btn--active`)}
            onClick={() => onChange(item.value)}
            onKeyDown={(event) => onKeyDown(event, idx)}
            data-testid={item['data-testid']}
          >
            {item.icon}
            {item.label}
          </button>
        );
      })}
    </div>
  );
};

const Radio: React.FC<FmSelectorProps> = ({
  value,
  onChange,
  items,
  label,
  labelledBy,
  className,
  'data-testid': dataTestId,
}) => {
  const { tabStop, onKeyDown, register } = useRovingSelection(items, value, onChange);
  const groupName = useId();
  return (
    <div
      role="radiogroup"
      aria-label={labelledBy ? undefined : label}
      aria-labelledby={labelledBy}
      className={cx('fm-selector-radio', className)}
      data-testid={dataTestId}
    >
      {items.map((item, idx) => {
        const isChecked = item.value === value;
        return (
          <label
            key={item.value}
            className={cx(
              'fm-selector-radio__option',
              item.disabled && 'fm-selector-radio__option--disabled',
            )}
            data-testid={item['data-testid']}
          >
            <input
              ref={register(idx)}
              type="radio"
              name={groupName}
              className="fm-selector-radio__input"
              checked={isChecked}
              disabled={item.disabled}
              aria-checked={isChecked}
              tabIndex={idx === tabStop ? 0 : -1}
              onChange={() => onChange(item.value)}
              onKeyDown={(event) => onKeyDown(event, idx)}
            />
            <span
              className={cx(
                'fm-selector-radio__control',
                isChecked && 'fm-selector-radio__control--checked',
              )}
              aria-hidden="true"
            >
              <span className="fm-selector-radio__dot" />
            </span>
            {item.icon}
            <span className="fm-selector-radio__label">{item.label}</span>
          </label>
        );
      })}
    </div>
  );
};

/**
 * FmSelector - controlled single-choice picker.
 *
 * @example
 * <FmSelector
 *   variant="segmented"
 *   value={year}
 *   onChange={setYear}
 *   label={t('Year')}
 *   items={[
 *     { value: '2025', label: t('year.2025') },
 *     { value: '2030', label: t('year.2030') },
 *   ]}
 * />
 */
export const FmSelector: React.FC<FmSelectorProps> = (props) => {
  const surface = useFmSurface();
  const tone = resolveSelectorTone(props, surface === 'glass-deep');
  if (props.variant === 'radio') return <Radio {...props} />;
  return <SegmentedOrChips {...props} variant={props.variant} tone={tone} />;
};

/** The tone a selector renders in; a tone the variant has no look for is refused. */
function resolveSelectorTone(props: FmSelectorProps, onGlassDeep: boolean): FmSelectorTone {
  if (props.tone !== undefined && !FM_SELECTOR_TONES.includes(props.tone)) {
    throw new Error(`FmSelector: tone "${String(props.tone)}" is not one of ${FM_SELECTOR_TONES.join(', ')}.`);
  }
  if (props.variant === 'radio') {
    if (props.tone === 'on-dark') {
      throw new Error('FmSelector: the radio variant has no on-dark tone; use segmented or chips on a deep surface.');
    }
    return 'default';
  }
  return props.tone ?? (onGlassDeep ? 'on-dark' : 'default');
}

export default FmSelector;
